import { expiryKind, requestReauth } from './reauth';

/** Thin fetch wrapper. Same-origin, cookie auth, CSRF header on every write. */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly detail?: Record<string, unknown>,
  ) {
    super(code);
  }
}

function csrfToken(): string {
  // `__Host-` so no other subdomain can plant it (see the Worker's lib/cookies.ts).
  const match = document.cookie.match(/(?:^|;\s*)__Host-ee_csrf=([^;]*)/);
  return match?.[1] ? decodeURIComponent(match[1]) : '';
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  try {
    return await send<T>(path, init);
  } catch (error) {
    // A lapsed session is offered a sign-in over the page, and the request sent once more after
    // it. Only once: a second refusal is a real one.
    if (!(error instanceof ApiError) || init.signal?.aborted) throw error;
    let kind = expiryKind(path, error.status, error.code);
    if (kind === 'admin-suspect') kind = (await adminSessionGone()) ? 'admin' : null;
    if (!kind || !(await requestReauth(kind))) throw error;
    return send<T>(path, init);
  }
}

/** True when the console has no session: `/admin/session` answers 404 exactly then. */
async function adminSessionGone(): Promise<boolean> {
  try {
    await send('/admin/session', {});
    return false;
  } catch (error) {
    return error instanceof ApiError && error.status === 404;
  }
}

async function send<T>(path: string, init: RequestInit): Promise<T> {
  const method = init.method ?? 'GET';
  const headers = new Headers(init.headers);
  // A FormData body must keep the browser's own multipart content-type: it carries the
  // boundary, and overriding it makes the Worker unable to parse the form at all.
  if (init.body && !(init.body instanceof FormData) && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  if (method !== 'GET' && method !== 'HEAD') headers.set('x-csrf-token', csrfToken());

  const response = await fetch(`/api${path}`, { ...init, method, headers, credentials: 'same-origin' });

  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    throw new ApiError(response.status, String(payload.error ?? 'request_failed'), payload);
  }
  return payload as T;
}

export const post = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });

export const patch = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) });

export const put = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) });

export const del = <T>(path: string) => api<T>(path, { method: 'DELETE' });
